import * as React from 'react';
import { CompactTable, Column as TableLibraryColumn } from '@table-library/react-table-library/compact';
import { useTheme } from '@table-library/react-table-library/theme';
import { DEFAULT_OPTIONS, getTheme } from '@table-library/react-table-library/mantine';
import { Collapse } from '@mantine/core';
import { da } from 'date-fns/locale';
import { useDictionary } from '../../contexts/dictionary';

// ------------------
// Define Custom Types
// ------------------

/**
 * Define the types of data you expect in each column.
 */
export type ColumnType = 'string' | 'number' | 'date' | 'boolean' | 'custom';

/**
 * Define your custom TableColumn interface.
 * This interface does NOT extend the library's Column<any> to avoid type conflicts.
 */
export interface TableColumn {
  id: string; // Unique identifier corresponding to data keys
  label: string; // Display label for the column header
  type?: ColumnType; // (Optional) Type of data for formatting purposes
  renderCell?: (item: any) => React.ReactNode; // (Optional) Custom cell rendering
  sumAddon?:string
}

// ------------------
// Define Component Props
// ------------------

interface TableComponentProps {
  data: Array<{ [key: string]: any }>;
  columns: TableColumn[]; // Use the custom TableColumn interface
  idColumn: string; // The key in data that uniquely identifies each row
  columnToShow?: number; // Optional: Number of columns to display in the table
  sumRow?: boolean; // Optional: Flag to show/hide the sum row
}

function sumNumericColumns(
  data: Array<{ [key: string]: any }>,
  columns: Array<{ id: string; type?: ColumnType, sumAddon?:string }>,
  translate: (key: string) => string
): Record<string, number | string> {
  const sums: Record<string, number | string> = {};

  const firstColumnId = columns[0]?.id;

  // Initialize sums
  for (const column of columns) {
    if (column.id === firstColumnId) {
      sums[column.id] = translate('total'); // First column shows "Total"
    }  else {
      sums[column.id] = 0; // Non-numbers are empty
    }
  }
 console.log('data!!', data)  
  // Calculate sums correctly
  for (const item of data) {
  
    for (const column of columns) {
      const value = item[column.id];
      console.log('value', value)
      if (!isNaN(value)) {
        sums[column.id] = (sums[column.id] as number) + Number(value);
      }
    }
  }
  for(let i in sums){
    const addon = columns.find((a)=>a.id===i)?.sumAddon || ""
    sums[i] = `${!isNaN(Number(sums[i]))?Number(sums[i]).toFixed(2):sums[i]} ${addon}` 

  }

  return sums;
}




const isTotalRow = (item: any) => {
  return item && Object.values(item).includes('Total');
};

// ------------------
// TableComponent
// ------------------

export const TableComponent: React.FC<TableComponentProps> = ({
  data,
  columns,
  idColumn,
  columnToShow,
  sumRow = false,
}) => {
  const {translate} = useDictionary()
  const totals = sumRow?sumNumericColumns(data, columns,translate):null;
  const nodes = { nodes: totals? [...data, totals]: data };

  // Define themes
  const mantineTheme = getTheme(DEFAULT_OPTIONS);
  const customTheme = {
    Table: `

      width: 100%;
      border-radius: 10px;
       th {
      text-align: right; /* Force header text to align to the right */
      direction: rtl;    /* Apply RTL direction to the headers */
    }
    
      td {
      
      padding: 5px 8px; /* control vertical and horizontal padding */
    }
      .animate {
        grid-column: 1 / -1;
        display: flex;
        
      }

      .animate > div {
        flex: 1;
        display: flex;
      }
      .clickable-row {
        cursor: pointer;
 
    }

    .clickable-row:hover {
      background-color: #f5f5f5; /* Light gray on hover */
    }
    `,
  };
  const theme = useTheme([mantineTheme, customTheme]);

  // State to manage expanded rows
  const [expandedIds, setExpandedIds] = React.useState<string[]>([]);

  // Handle row expansion toggle
  const handleExpand = (item: any) => {
    console.log(item)
    const itemId = item[idColumn];
    setExpandedIds((prevIds) => {
  
      return prevIds.includes(itemId)
      ? prevIds.filter((id) => id !== itemId)
      : [...prevIds, itemId];
    });
  };

  // Optionally limit the number of columns to show
  const displayedColumns = columnToShow
    ? columns.slice(0, columnToShow)
    : columns;

    const rowsColumns = (columnToShow ? columns.slice(columnToShow, columns.length): columns).map((a:any)=>a.id)
    ;

  // Map custom TableColumn to library's Column<any>, ensuring renderCell is provided
  const libraryColumns: TableLibraryColumn<any>[] = displayedColumns.map((col) => ({
    label: col.label,
    renderCell: (item: any) => {
      const value = item[col.id];
      const totalRow = isTotalRow(item);
    
      if (totalRow) {
        if (typeof value === 'number') {
          return <span style={{ fontWeight: 'bold' }}>{value.toLocaleString()}</span>;
        } else if (typeof value === 'string') {
          return <span style={{ fontWeight: 'bold'}}>{value}</span>;
        } else {
          return <span style={{ fontWeight: 'bold' }}></span>; // Empty cell
        }
      }
    
      // NORMAL ROWS
      if (col.renderCell) {
        return col.renderCell(item);
      }
    
      let displayValue: React.ReactNode;
      switch (col.type) {
        case 'date':
          displayValue = value ? new Date(value).toLocaleDateString('he-IL') : 'N/A';
          break;
        case 'boolean':
          displayValue = value ? 'Yes' : 'No';
          break;
        case 'number':
          displayValue =
            value !== null && value !== undefined
              ? value.toLocaleString()
              : 'N/A';
          break;
        default:
          displayValue =
            value !== null && value !== undefined ? value : 'N/A';
      }
    
      return <span>{displayValue}</span>;
    }
    
    
    
  }));
  

  // Define row properties, including click handler for expansion
  const rowProps = {
    onClick: (item: any) => {
      if (isTotalRow(item)) return; // Ignore click on Total row
      handleExpand(item);
    },
    className: 'clickable-row'
  };
  // Define how to render content after each row (expanded content)
  const rowOptions = {
    renderAfterRow: (item: any) => {
  
      return (

        <Collapse className="animate" in={expandedIds.includes(item[idColumn])} >
  <tr style={{ flex: '1', display: 'flex' }}>
    <td style={{ flex: '1' }} colSpan={displayedColumns.length} key={item.id}>
      <ul style={{ margin: '0', padding: '0', listStyleType: 'none' }}>
        {columns.map((column) => {
          const value = item[column.id];
          let displayValue: React.ReactNode;

          // Dynamically format the value based on the column type
          switch (column.type) {
            case 'date':
              displayValue = value
                ? new Date(value).toLocaleDateString('en-US')
                : 'N/A';
              break;
            case 'boolean':
              displayValue = value ? 'Yes' : 'No';
              break;
            case 'number':
              displayValue =
                value !== null && value !== undefined
                  ? value.toLocaleString()
                  : 'N/A';
              break;
            case 'custom':
              // If a custom render function is provided, use it
              displayValue = column.renderCell
                ? column.renderCell(item)
                : value;
              break;
            default:
              displayValue =
                value !== null && value !== undefined ? value : 'N/A';
          }

          // Make sure we only return the columns specified by rowsColumns
          if (!rowsColumns.includes(column.id)) return null;

          return (
            <li key={`${item[idColumn]}-${column.id}`}>  {/* Fix: Add a unique key */}
              <strong>{column.label}:</strong> {displayValue}
            </li>
          );
        })}
      </ul>
    </td>
  </tr>
</Collapse>
      )
    },
  };

  return (
    <CompactTable
      columns={libraryColumns}
      rowProps={rowProps}
      rowOptions={rowOptions}
      data={nodes}
      theme={theme}
      
     
    />
  );
};
